import { Button, Dropdown, Flex, MenuProps, Table, TableProps, Typography } from 'antd'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useSubmitIssuedCheckFile } from '../index.context'
import StatusTag from './StatusTag'
import dayjs from 'dayjs'
import { FORMAT_DATE_TIME } from 'src/app/shared/constants/app.constants'
import { formatNumberWithCommas } from 'src/app/shared/utils'
import '../index.style.scss'
import styled, { css } from 'styled-components'
import { useSelector } from 'react-redux'
import { getCriticalConfig } from 'src/app/store/masterSetup'
import { colorBgBrandless, colorText } from 'src/app/themes/primaryThemeDefault'
import { ActionWarningIcon, MoreCircleIcon } from 'src/assets/icons'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { ISSUED_CHECK_STATUS } from '../index.constant'

export interface CustomTableHeaderStyle {
  tableHeaderFontColor?: string
  tableHeaderBackgroundColor?: string
}

const WrapperTable: any = styled(Table<CustomTableHeaderStyle>)<CustomTableHeaderStyle>`
  .ant-table-thead .ant-table-cell {
    ${(props) =>
      props.tableHeaderFontColor &&
      css`
        color: ${props.tableHeaderFontColor};
      `}
    ${(props) =>
      props.tableHeaderBackgroundColor &&
      css`
        background: ${props.tableHeaderBackgroundColor};
      `}
  }
`

const ProcessingResults: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { checkList } = useSubmitIssuedCheckFile()
  const criticalConfig = useSelector(getCriticalConfig)

  const getHeaderName = useCallback(
    (key: string): string => {
      return t(`SubmitIssuedCheck:table.${key}`)
    },
    [t],
  )

  const getActionItems = useCallback(
    (rowData: any): MenuProps['items'] => {
      return [
        [ISSUED_CHECK_STATUS.PROCESSED_WITH_EXCEPTION, ISSUED_CHECK_STATUS.REJECTED, ISSUED_CHECK_STATUS.FAILED].includes(rowData?.status)
          ? {
              key: '1',
              label: (
                <Typography.Text style={{ fontSize: '14px' }}>
                  {t('IssuedCheckProcessingLog:result_screen.actions.view_error_details')}
                </Typography.Text>
              ),
              icon: <ActionWarningIcon />,
              onClick: () => {
                if (rowData?.id) navigate(`${menuUrls.viewErrorDetail}/${rowData?.id}`, { 
                  state: { returnUrl: window.location.pathname }
                })
              },
            }
          : null,
      ]
    },
    [t, navigate],
  )

  const columnDefs: TableProps<any>['columns'] = useMemo(
    (): TableProps<any>['columns'] => [
      {
        dataIndex: 'actions',
        key: 'actions',
        width: 80,
        title: '',
        render: (_: any, record: any) => {
          const items = getActionItems(record)
          const hasItems = (items?.filter(item => item !== null).length ?? 0) > 0
          return hasItems ? (
            <Dropdown trigger={['click']} menu={{ items }}>
              <Button
                className='button-more-action'
                icon={<MoreCircleIcon />}
              />
            </Dropdown>
          ) : null
        },
      },
      {
        dataIndex: 'no',
        key: 'no',
        width: 80,
        title: getHeaderName('no'),
      },
      {
        dataIndex: 'fileName',
        key: 'fileName',
        width: 320,
        title: getHeaderName('fileName'),
      },
      {
        dataIndex: 'updateDate',
        key: 'updateDate',
        width: 200,
        title: getHeaderName('updateDate'),
        render: (data: any) => {
          return (
            <Typography.Text
              style={{
                fontSize: 14,
                fontFamily: 'MyriadPro',
              }}
            >
              {dayjs(data).format(FORMAT_DATE_TIME)}
            </Typography.Text>
          )
        },
      },
      {
        dataIndex: 'status',
        key: 'status',
        width: 260,
        title: getHeaderName('status'),
        render: (data: any) => {
          return <StatusTag status={data} />
        },
      },
      {
        dataIndex: 'items',
        key: 'items',
        width: 96,
        title: getHeaderName('items'),
        render: (data: any) => {
          const formattedNumber = data?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
          return data !== null && data !== undefined ? (data > 0 ? formattedNumber : 0) : ''
        },
      },
      {
        dataIndex: 'amount',
        key: 'amount',
        width: 200,
        title: getHeaderName('amount'),
        render: (data: any) => {
          const formattedNumber = formatNumberWithCommas(data)
          return data !== null && data !== undefined ? (data > 0 ? formattedNumber : 0) : ''
        },
      },
    ],
    [getHeaderName, getActionItems],
  )

  if (!checkList) {
    return <div />
  }

  return (
    <div
      style={{
        marginTop: 24,
      }}
    >
      <Typography.Text className='issued-check-list-title'>
        {t('SubmitIssuedCheck:title.process_result')}
      </Typography.Text>
      <Flex>
        <WrapperTable
          columns={columnDefs}
          dataSource={[checkList].map((item, index) => ({ ...item, no: index + 1 }))}
          tableHeaderBackgroundColor={criticalConfig?.systemColor.tableHeaderBackgroundColor ?? colorBgBrandless}
          tableHeaderFontColor={criticalConfig?.systemColor.tableHeaderFontColor ?? colorText}
          pagination={false}
          scroll={{ x: 'max-content' }}
          style={{ width: '100%' }}
        />
        <div
          style={{
            height: 56,
            backgroundColor: criticalConfig?.systemColor.tableHeaderBackgroundColor ?? colorBgBrandless,
            flex: 1,
            borderTop: '1px solid #adb2b6',
          }}
        />
      </Flex>
    </div>
  )
}

export default ProcessingResults
